import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker/jalali";
import "react-day-picker/dist/style.css";
import dayjs from "dayjs";
import jalaliday from "jalaliday";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import persianJs from "@/lib/persian";
import { ChevronDownIcon, ChevronLeft, ChevronRight, ChevronUpIcon } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "./ui/button";
import Papa from "papaparse";
import DateObject from 'react-date-object'
import persian from "react-date-object/calendars/persian";
import arabic from "react-date-object/calendars/arabic";
import { getGregorianLabel, getHijriLabel } from "@/lib/utils";
import gregorian from "react-date-object/calendars/gregorian";
// فعال‌سازی تقویم جلالی
dayjs.extend(jalaliday);

interface CsvEvent {
  id: string;
  day_id: string;
  year: string;
  month: string;
  day: string;
  event: string;
  is_holiday: string;
  date: string;
}

interface ApiDay {
  solar: {
    day: number;
    month: number;
    year: number;
    dayWeek: string;
  };
  gregorian: {
    day: number;
    month: number;
    year: number;
    dayWeek: string;
  };
  moon: {
    day: number;
    month: number;
    year: number;
    dayWeek: string | null;
  };
  holiday: boolean;
  event: string[];
}

type ApiResponse = {
  status: boolean;
  result: Record<string, ApiDay>;
};

const gregorianMonths = [
  "",     // صفرم خالی تا ایندکس = شماره ماه باشه
  "Jan",  // 1
  "Feb",  // 2
  "Mar",  // 3
  "Apr",  // 4
  "May",  // 5
  "Jun",  // 6
  "Jul",  // 7
  "Aug",  // 8
  "Sep",  // 9
  "Oct",  // 10
  "Nov",  // 11
  "Dec"   // 12
];

// ماه‌های قمری (short = همون اسم رایج فارسی)
const hijriMonths = [
  "",            // صفرم خالی
  "محرم",        // 1
  "صفر",         // 2
  "ربیع‌الاول",  // 3
  "ربیع‌الثانی", // 4
  "جمادی‌الاول", // 5
  "جمادی‌الثانی",// 6
  "رجب",         // 7
  "شعبان",       // 8
  "رمضان",       // 9
  "شوال",        // 10
  "ذی‌القعده",   // 11
  "ذی‌الحجه"     // 12
];

// کامپوننت روز سفارشی
function CustomDay(props: any) {
  const { day: {date}, monthEvents, loading } = props;

  if (!props.children) {
    return <td></td>;
  }

  if (loading) {

    return (
      <td>
      <Skeleton className="size-10 rounded-full" />
      </td>
      )
  }

  // const jy = dayjs(date).calendar("jalali").year();
  // const jm = dayjs(date).calendar("jalali").month() + 1;
  const jd = persianJs( props.children).toEnglishNumber().toString();

  const isFriday = date.getDay() == 5
  // پیدا کردن روز از داده‌های API
  const todayData: ApiDay | undefined = monthEvents
    ? monthEvents.result?.[jd]
    : undefined;
  const gregorian = new DateObject(date)

  const hijri = new DateObject(date)
  hijri.convert(arabic)

  const today:ApiDay | undefined = {
    ...(monthEvents?.result?.[jd] || {}),
    gregorian: {
      day: gregorian.day,
      dayWeek: '',
      month: gregorian.month,
      year: gregorian.year,
    },
    moon: {
      day: hijri.day,
      dayWeek: '',
      month: hijri.month,
      year: hijri.year,
    },

  }
  const isHoliday = isFriday || todayData?.holiday;
  const eventsList = todayData?.event || [];


  const todayD = new Date();
  const isToday =
  date.getFullYear() === todayD.getFullYear() &&
  date.getMonth() === todayD.getMonth() &&
  date.getDate() === todayD.getDate();

  return (
    <td>
      <Popover>
        <PopoverTrigger asChild>
          <button
            {...props}
            className={`w-10 h-10 flex flex-col items-center justify-center rounded-full
              ${isToday ? 'bg-blue-500 text-white hover:bg-blue-600' :isHoliday ? "bg-red-300 text-red-500 hover:bg-red-400 cursor-pointer" : "hover:bg-card"}`}
          >
            <span>{props.children}</span>
            <span
              className={`size-1 rounded-full ${eventsList.length > 0 && "bg-foreground"}`}
            ></span>
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-60 p-0 overflow-hidden shadow-lg">
          <div className="bg-card rounded-t-md p-2">

          {eventsList.length > 0 ? (
            <ul className="text-sm mb-1">
              {eventsList.map((e: string, idx: number) => (
                <li key={idx}>
                  {isHoliday ? "🔴 " : "• "} {e}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm no-font mb-1">امروز خبر خاصی نیست...</p>
          )}
          </div>
          {today && <div className="flex justify-between items-center p-1">

          <p className="text-sm no-font text-foreground/50">
            {today.moon.day}/{hijriMonths[today.moon.month]}/{today.moon.year}
          </p>
          <p className="text-sm no-font text-foreground/50">
            {today.gregorian.year}/{gregorianMonths[today.gregorian.month]}/{today.gregorian.day}
          </p>
              </div>}
        </PopoverContent>
      </Popover>
    </td>
  );
}

// کامپوننت اصلی تقویم
export default function PersianCalendar() {
  const [allEvents, setAllEvents] = useState<CsvEvent[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date()); // شروع ماه
  const [loading, setLoading] = useState(false)


  // یک بار کل CSV رو بخون
  useEffect(() => {
    const local = localStorage.getItem('allEvents')
    if (typeof local == 'string') {
      const loaded = JSON.parse(local)
      setAllEvents(loaded as CsvEvent[])
      return
    }
    setLoading(true)
    fetch("/events.csv")
      .then((res) => res.text())
      .then((csvText) => {
        const parsed = Papa.parse(csvText, { header: true });
        setAllEvents(parsed.data as CsvEvent[]);
        localStorage.setItem('allEvents', JSON.stringify(parsed.data))
      }).finally(()=> {
        setLoading(false)
      });
  }, []);

  // دیتا برای ماه انتخابی
  const monthEvents: ApiResponse | null = (() => {
    if (allEvents.length === 0) return null;

    const jalaliDate = dayjs(currentMonth).calendar("jalali");
    const jy = jalaliDate.year();
    const jm = jalaliDate.month() + 1;

    // فقط رکوردهای این ماه و سال
    const filtered = allEvents.filter(
      (ev) => Number(ev.year) === jy && Number(ev.month) === jm
    );

    const result: Record<string, ApiDay> = {};
    filtered.forEach((ev) => {
      const sjy = Number(ev.year);
      const sjm = Number(ev.month);
      const sjd = Number(ev.day);

      // تاریخ میلادی با dayjs
      const gDate = new DateObject({ calendar: persian, year: sjy, month: sjm, day: sjd });
      gDate.convert(gregorian)
      const gYear = gDate.year;
      const gMonth = gDate.month.number;
      const gDay = gDate.day;
      

      // تاریخ قمری
      const hijri = new DateObject(gDate.toDate());
      const hYear = hijri.year;
      const hMonth = hijri.month.number;
      const hDay = hijri.day;
      

      const dayKey = ev.day;

      if (!result[dayKey]) {
        result[dayKey] = {
          solar: {
            day: sjd,
            month: sjm,
            year: sjy,
            dayWeek: "", // اگه خواستی از dayjs بگیری
          },
          gregorian: {
            day: gDay,
            month: gMonth,
            year: gYear,
            dayWeek: ''
          },
          moon: {
            day: hDay,
            month: hMonth,
            year: hYear,
            dayWeek: '',
          },
          holiday: false,
          event: [],
        };
      }

      result[dayKey].event.push(ev.event);
      if (ev.is_holiday === "1") {
        result[dayKey].holiday = true;
      }
    });

    return { status: true, result };
  })();

  if (loading) {
    return <>
      در حال آماده سازی ...
    </>
  }

  return (
    <DayPicker
      month={currentMonth}
      onMonthChange={setCurrentMonth} // تغییر ماه
      formatters={{
        formatWeekdayName(date) {
          const letters = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
          return letters[(date.getDay() + 1) % 7]; // شنبه = ش
        },
      }}
      components={{
        Day: (props) => <CustomDay {...props} monthEvents={monthEvents} loading={loading} />,
        MonthCaption: () => {
          // تاریخ جاری شمسی
          const current = dayjs(currentMonth).calendar("jalali");
          const jy = current.year();
          const jm = current.month() + 1;
        
          // تاریخ امروز شمسی
          const today = dayjs().calendar("jalali");
          const todayYear = today.year();
          const todayMonth = today.month() + 1;
        
          // لیست ماه‌های شمسی
          const months = [
            "فروردین","اردیبهشت","خرداد","تیر","مرداد","شهریور",
            "مهر","آبان","آذر","دی","بهمن","اسفند"
          ];
          const years = Array.from({ length: 50 }, (_, i) => jy - 25 + i);
        
          const gregorianLabel = getGregorianLabel(new DateObject({calendar:persian, year: jy, month: jm, day:1}));
        
          // قمری (از داده‌های API)
          let hijriLabel = getHijriLabel(new DateObject({calendar:persian, year: jy, month: jm, day:1}));
          
        
          const isToday =
            jy === todayYear && jm === todayMonth;
        
          return (
            <div className="flex flex-col items-start gap-1">
              {/* انتخاب ماه و سال شمسی */}
              <div className="flex gap-2 items-start">
                <Select
                  value={jm.toString()}
                  onValueChange={(val) => {
                    const newDate = dayjs(currentMonth)
                      .calendar("jalali")
                      .year(jy)
                      .month(parseInt(val) - 1)
                      .toDate();
                    setCurrentMonth(newDate);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ماه" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
        
                <Select
                  value={jy.toString()}
                  onValueChange={(val) => {
                    const newDate = dayjs(currentMonth)
                      .calendar("jalali")
                      .year(parseInt(val))
                      .month(jm - 1)
                      .toDate();
                    setCurrentMonth(newDate);
                  }}
                >
                  <SelectTrigger className="p-1">
                    <SelectValue placeholder="سال" className="text-sm no-font" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
        
                {/* دکمه امروز */}
                {!isToday && (
                  <Button
                    size='sm'
                    variant="outline"
                    onClick={() => setCurrentMonth(dayjs().toDate())}
                  >
                    امروز
                  </Button>
                )}
              </div>
        
              {/* لیبل میلادی و قمری زیر سلکتورها */}
              <div className="flex items-start gap-2 text-xs no-font text-foreground/60">
                {hijriLabel && <span>{hijriLabel}</span>}
                <span>{gregorianLabel}</span>
              </div>
            </div>
          );
        },
        
        Chevron(props) {
          switch(props.orientation) {
            case 'up':
              return <ChevronUpIcon />
            case 'down':
              return <ChevronDownIcon />
            case 'left':
              return <ChevronRight />
            case 'right':
              return <ChevronLeft />
            default:
              return <></>
          }
        },
      }}
    />
  );
}
